import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '@/lib/supabase';
import { reportSwallowed } from '@/lib/sentry';

// ============================================================================
// 🚨 DEPRECATED - Phase 2 Refactor
// ============================================================================
// This file is deprecated in favor of src/stores/clientProgramStore.ts
//
// The new Zustand store provides:
// - Centralized program, completion, and metrics state
// - Real-time Supabase subscriptions (no polling)
// - Optimistic UI updates
// - Better caching and performance
//
// Migration path:
// - Use `useProgram()` from @/stores/clientProgramStore.selectors instead of calling this directly
// - Use `useClientDashboardData()` for unified program + completions data
//
// This file is kept temporarily for backwards compatibility during the migration.
// Will be removed in a future phase once all consumers are migrated.
// ============================================================================

// ============================================================================
// CACHE LAYER - Prevents duplicate fetches
// ============================================================================
const CACHE_TTL_MS = 30000; // 30 seconds
let programCache: { data: Program | null; timestamp: number } | null = null;

export function clearProgramCache() {
  programCache = null;
}

// Mock-era shapes the old UI expects
export type ProgramDay = {
  dayId?: string; // <-- add (present in real mode)
  date: string; // YYYY-MM-DD (local)
  type: 'workout' | 'rest';
  title: string;
  durationMin?: number;
  workoutId?: string | null;
  exerciseSnapshot?: any[]; // from exercise_snapshot for snapshot-first
  snapshot_meta?: any; // metadata for custom workouts (notes, modality, etc.)
};

export type Program = {
  id: string;
  name: string;
  start: string; // YYYY-MM-DD (program start)
  totalWeeks: number;
  days: ProgramDay[]; // 1 entry per "scheduled" calendar day
  workouts?: Map<string, any>; // Loaded workout data by ID
};

function toLocalISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startSunday(startISO: string): Date {
  // 0 = Sun; anchor to the Sunday of the week containing startISO
  const start = new Date(`${startISO}T00:00:00`);
  const sunday = new Date(start);
  sunday.setDate(start.getDate() - start.getDay());
  return sunday;
}

function computeDateFromWeekday(startISO: string, weekNumber: number, weekday: number): string {
  // weekday: 0-6 (0=Sunday, 6=Saturday)
  const base = startSunday(startISO);
  const d = new Date(base);
  d.setDate(base.getDate() + (weekNumber - 1) * 7 + weekday); // No -1, weekday is already 0-6
  return toLocalISODate(d);
}

export async function getClientProgramCompat(sb?: SupabaseClient, forceRefresh = false): Promise<Program | null> {
  // Check cache first (unless force refresh)
  if (!forceRefresh && programCache) {
    const age = Date.now() - programCache.timestamp;
    if (age < CACHE_TTL_MS) {
      return programCache.data;
    }
  }

  // Try Worker endpoint first for proper httpOnly cookie authentication
  let assignment: any = null;
  let days: any[] = [];

  try {
    const response = await fetch('/api/clients/programs');
    if (response.ok) {
      const responseData = await response.json();
      const { assignment: workerAssignment, days: workerDays } = responseData;
      assignment = workerAssignment;
      days = workerDays || [];
    } else {
      throw new Error('Worker endpoint failed');
    }
  } catch (_workerError) {
    const supabase = sb ?? getSupabase();
    if (!supabase) {
      return null;
    }

    // 1) latest active assignment for THIS logged-in client (RLS will scope it)
    let a: any;
    try {
      // Add timeout to prevent hanging
      const queryPromise = supabase
        .from('client_programs')
        .select(
          `
          id,
          display_name,
          total_weeks_override,
          start_date,
          programs:program_id (
            id,
            name,
            level,
            total_weeks,
            goal
          )
        `,
        )
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Query timeout')), 10000),
      );

      a = await Promise.race([queryPromise, timeoutPromise]);

      if (!a?.data) {
        return null;
      }
      assignment = a.data as {
        id: string;
        display_name: string | null;
        total_weeks_override: number | null;
        start_date: string | null;
      };

      // 2) all days for that assignment
      const d = await supabase
        .from('client_program_days')
        .select('id, week_number, weekday, title, exercise_snapshot, snapshot_meta, workout_id')
        .eq('client_program_id', assignment.id)
        .order('week_number')
        .order('weekday');

      days = d.data ?? [];
    } catch (e) {
      console.error('🚫 getClientProgramCompat: Assignment query failed:', e);
      return null;
    }
  }

  if (!assignment) {
    return null;
  }

  const rows = days as any[];

  // Debug: Check what's in the raw database rows (remove this in production)
  // console.log(`[getClientProgramCompat] Raw database rows:`, { ... });

  // Enrich snapshot items with names (runtime quick win)
  const snapIds = new Set<string>();
  for (const r of rows) {
    const items = Array.isArray((r as any).exercise_snapshot) ? (r as any).exercise_snapshot : [];
    for (const it of items) {
      const exId = (it && (it.exercise_id || it.exerciseId)) as string | undefined;
      if (typeof exId === 'string') snapIds.add(exId);
    }
  }

  let nameById = new Map<string, string>();
  if (snapIds.size) {
    try {
      const ex = await sb!.from('exercises').select('id,name').in('id', Array.from(snapIds));
      if (!(ex as any).error) {
        nameById = new Map(((ex.data ?? []) as any[]).map((e: any) => [e.id, e.name]));
      }
    } catch {
      // If denied by RLS, leave map empty and fall back to 'Exercise'
    }
  }
  // Collect unique workout IDs to fetch
  const workoutIds = new Set<string>();
  for (const r of rows) {
    if (r.workout_id) workoutIds.add(r.workout_id);
  }

  // Fetch workout data for linked workouts
  const workoutsMap = new Map<string, any>();
  if (workoutIds.size > 0) {
    try {
      const { getWorkoutById } = await import('@/data/storage/workouts/supabase');
      const workoutPromises = Array.from(workoutIds).map(async (id) => {
        try {
          const workout = await getWorkoutById(id);
          if (workout) {
            // Debug: Check what we got from getWorkoutById (remove this in production)
            // console.log(`[getClientProgramCompat] Loaded workout ${id}:`, { ... });

            // Transform to the format selectWorkoutByDay expects
            const transformedExercises =
              workout.items?.map((item: any) => ({
                id: item.exerciseId,
                name: item.exercise?.name || 'Exercise',
                sets: item.sets,
                reps: item.reps,
                duration: item.durationSec,
                rest: item.restSec,
                // Add other fields as needed
              })) || [];

            // Debug: Check transformed exercises (remove this in production)
            // console.log(`[getClientProgramCompat] Transformed exercises for ${id}:`, { ... });

            return {
              id: workout.id,
              name: workout.name,
              modality: workout.modality,
              notes: workout.notes,
              exercises: transformedExercises,
            };
          }
          return null;
        } catch (e) {
          console.warn(`[getClientProgramCompat] Failed to load workout ${id}:`, e);
          return null;
        }
      });

      const workouts = await Promise.all(workoutPromises);
      for (const workout of workouts) {
        if (workout) {
          workoutsMap.set(workout.id, workout);
        }
      }
    } catch (e) {
      console.warn('[getClientProgramCompat] Failed to load workouts:', e);
      reportSwallowed('getClientProgramCompat.loadWorkouts', e);
    }
  }

  const maxWeek = rows.reduce((m, r: any) => Math.max(m, r.week_number ?? 0), 0);

  // Extract program data from the nested programs object (from foreign key join)
  const programData = assignment.programs || {};
  const totalWeeks = assignment.total_weeks_override ?? programData.total_weeks ?? (maxWeek || 1);
  const startISO = assignment.start_date ?? toLocalISODate(new Date());

  // 3) Map DB rows → mock ProgramDay shape (snapshot-first)
  const daysRaw: ProgramDay[] = rows.map((r: any) => {
    const hasSnapshot = Array.isArray(r.exercise_snapshot) && r.exercise_snapshot.length > 0;
    const snapshotWithNames = hasSnapshot
      ? r.exercise_snapshot.map((it: any) => {
          // Get exercise name from nameById map, or from denormalized name in snapshot, or fallback
          const exerciseName =
            (typeof it?.exercise_id === 'string' ? nameById.get(it.exercise_id) : null) || it.name || 'Exercise';

          // Preserve all exercise properties for proper display
          return {
            ...it,
            name: exerciseName,
            exercise_id: it.exercise_id || it.exerciseId || null,
            // Ensure trackBy is set (default to 'reps' if missing)
            trackBy: it.track_by || it.trackBy || 'reps',
            // Map snake_case to camelCase for consistency
            track_by: it.track_by || it.trackBy || 'reps',
            durationSec: it.duration_sec ?? it.durationSec ?? null,
            duration_sec: it.duration_sec ?? it.durationSec ?? null,
            restSec: it.rest_sec ?? it.restSec ?? null,
            rest_sec: it.rest_sec ?? it.restSec ?? null,
            workSec: it.work_sec ?? it.workSec ?? null,
            work_sec: it.work_sec ?? it.workSec ?? null,
            distance: it.distance ?? null,
            distanceUnit: it.distance_unit ?? it.distanceUnit ?? null,
            distance_unit: it.distance_unit ?? it.distanceUnit ?? null,
            rounds: it.rounds ?? null,
            tempo: it.tempo ?? null,
            effort: it.effort ?? null,
            orderIndex: it.order_index ?? it.orderIndex ?? null,
            order_index: it.order_index ?? it.orderIndex ?? null,
          };
        })
      : undefined;
    const title =
      (r.snapshot_meta && (r.snapshot_meta as any).name) || r.title || (r.workout_id ? 'Linked workout' : 'Workout');
    const durationMin =
      r.snapshot_meta && typeof (r.snapshot_meta as any).duration_min === 'number'
        ? (r.snapshot_meta as any).duration_min
        : undefined;

    const mappedDay: ProgramDay = {
      dayId: r.id, // ✅ add this
      date: computeDateFromWeekday(startISO, r.week_number, r.weekday),
      type: hasSnapshot || r.workout_id ? 'workout' : 'rest',
      title,
      durationMin,
      workoutId: r.workout_id,
      exerciseSnapshot: snapshotWithNames,
      snapshot_meta: r.snapshot_meta, // ✅ Pass through snapshot_meta
    };

    return mappedDay;
  });

  // 4) Pad to full calendar coverage so selectors can assume 7 days/week
  const padded: ProgramDay[] = [];
  const dayMap = new Map(daysRaw.map((d) => [d.date, d]));
  for (let w = 1; w <= totalWeeks; w++) {
    for (let wd = 0; wd <= 6; wd++) {
      // 0=Sunday, 6=Saturday
      const date = computeDateFromWeekday(startISO, w, wd);
      const found = dayMap.get(date);
      padded.push(found ?? { date, type: 'rest', title: 'Rest' });
    }
  }

  const result = {
    id: assignment.id,
    name: assignment.display_name ?? programData.name ?? 'Program',
    start: startISO,
    totalWeeks,
    days: padded,
    workouts: workoutsMap,
  };

  // Store in cache before returning
  programCache = { data: result, timestamp: Date.now() };

  return result;
}
